import { createSelector, Selector } from '@reduxjs/toolkit'
import { EntityType, TradingEntityMeta, TradingEntityMetaDetail, CrudEventEnum } from 'api'
import { GlobalState } from 'store'
import { TradingEntityHelper } from 'model'
import { createAllEntitiesSelector, createEntityGetterSelector, createAllEntityUpdatesSelector } from '../entitySelectors'
import { tradingRelationshipsGetterSelector } from '../tradingRelationshipSelectors'
import { NEW_ITEM_PREFIX } from 'utils'
import { createAuthorisedUserSelector } from '../connectionSelectors'
import { sortBy, uniq } from 'lodash-es'

export const existingTradingEntityMetaGetterSelector: Selector<
  GlobalState,
  (tradingEntityId: string) => TradingEntityMeta[]
> = createSelector(createAllEntitiesSelector(EntityType.TRADING_ENTITY_META), allTradingEntityMeta => (tradingEntityId: string) =>
  allTradingEntityMeta.filter(tradingEntityMeta => tradingEntityMeta.ownerTradingEntityId === tradingEntityId)
)

const missingTradingEntityMetaGetterSelector: Selector<GlobalState, (tradingEntityId: string) => TradingEntityMeta[]> = createSelector(
  existingTradingEntityMetaGetterSelector,
  tradingRelationshipsGetterSelector,
  (getExistingTradingEntityMeta, getTradingRelationships) => (tradingEntityId: string) => {
    const existingTradingEntityMeta = getExistingTradingEntityMeta(tradingEntityId)

    const tradingRelationships = getTradingRelationships(tradingEntityId)
    const relatedCounterpartyIdsWithoutMeta = uniq(
      tradingRelationships
        .map(tradingRelationship =>
          tradingRelationship.buyTradingEntity === tradingEntityId
            ? tradingRelationship.sellTradingEntity
            : tradingRelationship.buyTradingEntity
        )
        .filter(
          counterpartyId =>
            !existingTradingEntityMeta.map(tradingEntityMeta => tradingEntityMeta.subjectTradingEntityId).includes(counterpartyId)
        )
    )

    return relatedCounterpartyIdsWithoutMeta.map(counterpartyId => ({
      id: `${NEW_ITEM_PREFIX}-${tradingEntityId}-${counterpartyId}`,
      aliasTypeAndValues: [],
      ownerTradingEntityId: tradingEntityId,
      subjectTradingEntityId: counterpartyId,
      primaryCoverUserId: null,
      secondaryCoverUserId: null,
      primeBrokerId: null
    }))
  }
)

export const tradingEntityMetasGetterSelector: Selector<GlobalState, (tradingEntityId: string) => TradingEntityMeta[]> = createSelector(
  existingTradingEntityMetaGetterSelector,
  missingTradingEntityMetaGetterSelector,
  createEntityGetterSelector(EntityType.TRADING_ENTITY),
  (getExistingTradingEntityMeta, getMissingTradingEntityMeta, getTradingEntity) => (tradingEntityId: string) => {
    const existingTradingEntityMeta = getExistingTradingEntityMeta(tradingEntityId)
    const missingTradingEntityMeta = getMissingTradingEntityMeta(tradingEntityId)

    return sortBy(existingTradingEntityMeta.concat(missingTradingEntityMeta), tradingEntityMetaItem => {
      const subjectTradingEntity = getTradingEntity(tradingEntityMetaItem.subjectTradingEntityId)
      return subjectTradingEntity ? TradingEntityHelper.getName(subjectTradingEntity) : ''
    })
  }
)

export const tradingEntityMetaGetterSelector: Selector<
  GlobalState,
  (ownerTradingEntityId: string, subjectTradingEntityId: string) => TradingEntityMeta | null
> = createSelector(
  createAllEntitiesSelector(EntityType.TRADING_ENTITY_META),
  allTradingEntityMetas => (ownerTradingEntityId: string, subjectTradingEntityId: string) => {
    return (
      allTradingEntityMetas.find(
        tradingEntityMeta =>
          tradingEntityMeta.ownerTradingEntityId === ownerTradingEntityId &&
          tradingEntityMeta.subjectTradingEntityId === subjectTradingEntityId
      ) ?? null
    )
  }
)

const existingTradingEntityMetaDetailsSelector: Selector<GlobalState, TradingEntityMetaDetail[]> = createSelector(
  createAllEntityUpdatesSelector(EntityType.TRADING_ENTITY_META),
  createEntityGetterSelector(EntityType.TRADING_ENTITY),
  createEntityGetterSelector(EntityType.ORGANISATION),
  createEntityGetterSelector(EntityType.USER),
  createEntityGetterSelector(EntityType.PRIME_BROKER),
  (tradingEntityMetaUpdates, getTradingEntity, getOrganisation, getUser, getPrimeBroker) => {
    return tradingEntityMetaUpdates.map(update => {
      const tradingEntityMeta = update.entity
      const ownerTradingEntity = getTradingEntity(tradingEntityMeta.ownerTradingEntityId)
      const subjectTradingEntity = getTradingEntity(tradingEntityMeta.subjectTradingEntityId)
      const subjectOrganisation = subjectTradingEntity ? getOrganisation(subjectTradingEntity.organisationId) : null
      const availableEvents = update.availableEvents
      const primaryCoverUser = tradingEntityMeta.primaryCoverUserId ? getUser(tradingEntityMeta.primaryCoverUserId) : null
      const secondaryCoverUser = tradingEntityMeta.secondaryCoverUserId ? getUser(tradingEntityMeta.secondaryCoverUserId) : null
      const primeBroker = tradingEntityMeta.primeBrokerId ? getPrimeBroker(tradingEntityMeta.primeBrokerId) : null

      return {
        tradingEntityMeta,
        ownerTradingEntity,
        subjectTradingEntity,
        subjectOrganisation,
        availableEvents,
        primaryCoverUser,
        secondaryCoverUser,
        primeBroker
      }
    })
  }
)

export const tradingEntityMetaDetailsSelector: Selector<GlobalState, TradingEntityMetaDetail[]> = createSelector(
  createAuthorisedUserSelector(),
  existingTradingEntityMetaDetailsSelector,
  missingTradingEntityMetaGetterSelector,
  createEntityGetterSelector(EntityType.TRADING_ENTITY),
  createEntityGetterSelector(EntityType.ORGANISATION),
  createEntityGetterSelector(EntityType.USER),
  createEntityGetterSelector(EntityType.PRIME_BROKER),
  (
    authorisedUser,
    existingTradingEntityMetaDetails,
    getMissingTradingEntityMeta,
    getTradingEntity,
    getOrganisation,
    getUser,
    getPrimeBroker
  ) => {
    const missingTradingEntityMetaDetails = authorisedUser.tradingEntityDetails.reduce((tradingEntityMetaDetails, { tradingEntityId }) => {
      const missingMeta = getMissingTradingEntityMeta(tradingEntityId)
      const missingMetaDetails: TradingEntityMetaDetail[] = missingMeta.map(tradingEntityMeta => {
        const subjectTradingEntity = getTradingEntity(tradingEntityMeta.subjectTradingEntityId)
        return {
          tradingEntityMeta,
          ownerTradingEntity: getTradingEntity(tradingEntityMeta.ownerTradingEntityId),
          subjectTradingEntity,
          subjectOrganisation: subjectTradingEntity ? getOrganisation(subjectTradingEntity.organisationId) : null,
          primaryCoverUser: tradingEntityMeta.primaryCoverUserId ? getUser(tradingEntityMeta.primaryCoverUserId) : null,
          secondaryCoverUser: tradingEntityMeta.secondaryCoverUserId ? getUser(tradingEntityMeta.secondaryCoverUserId) : null,
          primeBroker: tradingEntityMeta.primeBrokerId ? getPrimeBroker(tradingEntityMeta.primeBrokerId) : null,
          availableEvents: [CrudEventEnum.UPSERT]
        }
      })
      return tradingEntityMetaDetails.concat(missingMetaDetails)
    }, [] as TradingEntityMetaDetail[])

    return sortBy(existingTradingEntityMetaDetails.concat(missingTradingEntityMetaDetails), detail =>
      detail.subjectTradingEntity ? TradingEntityHelper.getName(detail.subjectTradingEntity) : ''
    )
  }
)

export const tradingEntityMetasContainingAliasesSelector: Selector<GlobalState, TradingEntityMeta[]> = createSelector(
  createAuthorisedUserSelector(),
  existingTradingEntityMetaGetterSelector,
  (authorisedUser, getOwnerTradingEntityMeta) => {
    return authorisedUser.tradingEntityDetails.flatMap(({ tradingEntityId: ownerTradingEntityId }) => {
      const ownerTradingEntityMetas = getOwnerTradingEntityMeta(ownerTradingEntityId)

      const ownerMetaContainingAliases = ownerTradingEntityMetas.filter(m => m?.aliasTypeAndValues?.length >= 1)

      return ownerMetaContainingAliases
    })
  }
)

export const tradingEntityMetasContainingPrimeBrokerSelector: Selector<GlobalState, TradingEntityMeta[]> = createSelector(
  createAuthorisedUserSelector(),
  existingTradingEntityMetaGetterSelector,
  (authorisedUser, getOwnerTradingEntityMeta) => {
    return authorisedUser.tradingEntityDetails.flatMap(({ tradingEntityId: ownerTradingEntityId }) => {
      const ownerTradingEntityMetas = getOwnerTradingEntityMeta(ownerTradingEntityId)

      const ownerMetaContainingPrimeBroker: TradingEntityMeta[] = ownerTradingEntityMetas.filter(m => m?.primeBrokerId)

      return ownerMetaContainingPrimeBroker
    })
  }
)

export const tradingEntityMetaByAliasGetterSelector: Selector<
  GlobalState,
  (alias: string, ownerTradingEntityId: string) => TradingEntityMeta | null
> = createSelector(tradingEntityMetasContainingAliasesSelector, metas => (alias: string, ownerTradingEntityId: string) => {
  return metas.find(m => m?.aliasTypeAndValues?.some(a => a.value === alias) && m.ownerTradingEntityId === ownerTradingEntityId) ?? null
})

export const tradingEntityMetaDetailBySubjectTradingEntityIdGetterSelector: Selector<
  GlobalState,
  (subjectTradingEntityId: string) => TradingEntityMetaDetail[]
> = createSelector(tradingEntityMetaDetailsSelector, tradingEntityMetaDetails => (subjectTradingEntityId: string) => {
  return tradingEntityMetaDetails.filter(entityDetail => entityDetail.tradingEntityMeta?.subjectTradingEntityId === subjectTradingEntityId)
})

export const defaultPrimeBrokerIdGetterSelector: Selector<
  GlobalState,
  (ownerTradingEntityId: string, subjectTradingEntityId: string) => string | null
> = createSelector(
  createAllEntitiesSelector(EntityType.TRADING_ENTITY_META),
  allTradingEntityMetas => (ownerTradingEntityId: string, subjectTradingEntityId: string) =>
    allTradingEntityMetas.find(
      tradingEntityMeta =>
        tradingEntityMeta.ownerTradingEntityId === ownerTradingEntityId &&
        tradingEntityMeta.subjectTradingEntityId === subjectTradingEntityId
    )?.primeBrokerId ?? null
)
